import PlayerState from '@/services/player/PlayerState';
import utilities from '@/services/utilities/utilities';
import constants from '@/services/constants';
import _ from 'lodash';

export default class Player {
  constructor() {
    this.id = _.uniqueId();
    this.weightToApplyToAddedHands = 100;
    this.combosToAdd = [
      'cc', 'cs', 'ch', 'cd',
      'sc', 'ss', 'sh', 'sd',
      'hc', 'hs', 'hh', 'hd',
      'dc', 'ds', 'dh', 'dd'
    ];
    this.states = [
      new PlayerState('afterPreflop'),
      new PlayerState('afterFlop'),
      new PlayerState('afterTurn'),
      new PlayerState('afterRiver'),
    ];
    this._setNextStateProperties();
    this._setPreviousStateProperties();
  };

  // constructor helpers
  _setNextStateProperties() {
    this.states[0].nextState = this.states[1];
    this.states[1].nextState = this.states[2];
    this.states[2].nextState = this.states[3];
    this.states[3].nextState = null;
  };
  _setPreviousStateProperties() {
    this.states[0].previousState = null;
    this.states[1].previousState = this.states[0];
    this.states[2].previousState = this.states[1];
    this.states[3].previousState = this.states[2];
  };

  // hands
  selectGeneralHands(generalHandStrings, presetCommunityCards, playerStateIndex, shortDeck) {
    generalHandStrings.forEach(function (generalHandString) {
      this.selectGeneralHand(generalHandString, presetCommunityCards, playerStateIndex, shortDeck);
    }, this);
  };
  selectGeneralHand(generalHandString, presetCommunityCards, playerStateIndex, shortDeck) {
    let generalHand = this.states[playerStateIndex].generalHands.find(generalHandString);
    let specificHandStringsToSelect = generalHand.getSpecificHandStringsToSelect(this.combosToAdd);

    specificHandStringsToSelect.forEach(function (specificHandString) {
      this.selectSpecificHand(specificHandString, presetCommunityCards, playerStateIndex, shortDeck);
    }, this);
  };
  selectSpecificHand(specificHandString, presetCommunityCards, playerStateIndex, shortDeck) {
    this.setWeightOfSpecificHand(specificHandString, this.weightToApplyToAddedHands, presetCommunityCards, playerStateIndex, shortDeck);
  };
  selectSpecificHands(specificHandStrings, presetCommunityCards, playerStateIndex, shortDeck) {
    let specificHand;

    specificHandStrings.forEach(function (specificHandString) {
      specificHand = this.states[playerStateIndex].generalHands.find(specificHandString);

      if (specificHand.availableToSelect(this.combosToAdd)) {
        this.selectSpecificHand(specificHandString, presetCommunityCards, playerStateIndex, shortDeck);
      }
    }, this);
  };
  manuallySelectGeneralHand(generalHandString, presetCommunityCards, playerStateIndex, shortDeck) {
    if (playerStateIndex === 0) {
      throw new Error('Trying to call manuallySelectGeneralHand for the afterPreflop state.');
    }

    let generalHand = this.states[playerStateIndex].generalHands.find(generalHandString);
    let specificHandStringsToSelect = generalHand.getSpecificHandStringsToSelect(this.combosToAdd);

    specificHandStringsToSelect.forEach(function (specificHandString) {
      this.manuallySelectSpecificHand(specificHandString, presetCommunityCards, playerStateIndex, shortDeck);
    }, this);
  };
  manuallySelectSpecificHand(specificHandString, presetCommunityCards, playerStateIndex, shortDeck) {
    if (playerStateIndex === 0) {
      throw new Error('Trying to call manuallySelectSpecificHand for the afterPreflop state.');
    }

    this.manuallySetWeightOfSpecificHand(specificHandString, this.weightToApplyToAddedHands, presetCommunityCards, playerStateIndex, shortDeck);
  };
  setSpecificHandAsManuallySet(specificHandString, playerStateIndex) {
    let specificHand = this.states[playerStateIndex].generalHands.find(specificHandString);

    if (specificHand.inRange) {
      specificHand.manuallySet = 'added';
    } else {
      specificHand.manuallySet = 'removed';
    }
  };
  setSpecificHandAsNotManuallySet(specificHandString, playerStateIndex) {
    let specificHand = this.states[playerStateIndex].generalHands.find(specificHandString);

    specificHand.manuallySet = false;
  };
  deselectGeneralHand(generalHandString, presetCommunityCards, playerStateIndex, shortDeck) {
    let generalHand = this.states[playerStateIndex].generalHands.find(generalHandString);

    generalHand.forEachSpecificHandInRange(function (specificHand) {
      this.deselectSpecificHand(specificHand.string, presetCommunityCards, playerStateIndex, shortDeck);
    }, this);
  };
  deselectGeneralHands(generalHandStrings, presetCommunityCards, playerStateIndex, shortDeck) {
    generalHandStrings.forEach(function (generalHandString) {
      this.deselectGeneralHand(generalHandString, presetCommunityCards, playerStateIndex, shortDeck);
    }, this);
  };
  deselectAllGeneralHands(presetCommunityCards, playerStateIndex, shortDeck) {
    let allGeneralHandStrings = constants.handRankings['sklansky-chubukov'];

    this.deselectGeneralHands(allGeneralHandStrings, presetCommunityCards, playerStateIndex, shortDeck);
  };
  deselectSpecificHand(specificHandString, presetCommunityCards, playerStateIndex, shortDeck) {
    this.setWeightOfSpecificHand(specificHandString, 0, presetCommunityCards, playerStateIndex, shortDeck);
  };
  deselectAllSpecificHandsThatUseCardAndSetAsImpossible(card, presetCommunityCards, shortDeck) {
    let specificHandStringsThatUseCard = utilities.hands.getAllSpecificHandStringsUsingCard(card);
    let specificHandForAfterPreflop;

    specificHandStringsThatUseCard.forEach(function (specificHandString) {
      specificHandForAfterPreflop = this.states[0].generalHands.find(specificHandString);

      if (specificHandForAfterPreflop.possible && specificHandForAfterPreflop.inRange) {
        this.deselectSpecificHand(specificHandString, presetCommunityCards, 0, shortDeck);
      }

      if (specificHandForAfterPreflop.possible) {
        this.states[0].generalHands.numberOfPossibleSpecificHands--; // maxPossibleWeight will always be 100 because it's the afterPreflop range
        specificHandForAfterPreflop.maxPossibleWeight = 0;
      }
    }, this);
  };
  manuallyDeselectGeneralHand(generalHandString, presetCommunityCards, playerStateIndex, shortDeck) {
    if (playerStateIndex === 0) {
      throw new Error('Trying to call manuallyDeselectGeneralHand for the afterPreflop state.');
    }

    let generalHand = this.states[playerStateIndex].generalHands.find(generalHandString);

    generalHand.forEachSpecificHandInRange(function (specificHand) {
      this.manuallyDeselectSpecificHand(specificHand.string, presetCommunityCards, playerStateIndex, shortDeck);
    }, this);
  };
  manuallyDeselectAllGeneralHands(presetCommunityCards, playerStateIndex, shortDeck) {
    let allGeneralHandStrings = constants.handRankings['sklansky-chubukov'];

    allGeneralHandStrings.forEach(function (generalHandString) {
      this.manuallyDeselectGeneralHand(generalHandString, presetCommunityCards, playerStateIndex, shortDeck);
    }, this);
  };
  manuallyDeselectSpecificHand(specificHandString, presetCommunityCards, playerStateIndex, shortDeck) {
    if (playerStateIndex === 0) {
      throw new Error('Trying to call manuallyDeselectSpecificHand for the afterPreflop state.');
    }

    this.manuallySetWeightOfSpecificHand(specificHandString, 0, presetCommunityCards, playerStateIndex, shortDeck);
  };

  applyExactWeightToGeneralHand(generalHandString, newWeight, presetCommunityCards, playerStateIndex, shortDeck) {
    let generalHand = this.states[playerStateIndex].generalHands.find(generalHandString);

    generalHand.forEachSpecificHandInRange(function (specificHand) {
      this.setWeightOfSpecificHand(specificHand.string, newWeight, presetCommunityCards, playerStateIndex, shortDeck);
    }, this);
  };
  setWeightOfSpecificHand(specificHandString, newWeight, presetCommunityCards, playerStateIndex, shortDeck) {
    let specificHandOfCurrentState;
    let specificHandOfNextState;

    for (let i = playerStateIndex; i < this.states.length; i++) {
      this.states[i].setWeightOfSpecificHand(specificHandString, newWeight);

      if (!this.states[i].nextState) {
        break;
      }

      specificHandOfCurrentState = this.states[i].generalHands.find(specificHandString);
      specificHandOfNextState = this.states[i].nextState.generalHands.find(specificHandString);
      newWeight = specificHandOfNextState.getWeightToApply(this.states[i].nextState, presetCommunityCards, specificHandOfCurrentState.weight, shortDeck);
    }
  };
  manuallyApplyExactWeightToGeneralHand(generalHandString, newWeight, presetCommunityCards, playerStateIndex, shortDeck) {
    if (playerStateIndex === 0) {
      throw new Error('Trying to call manuallyApplyExactWeightToGeneralHand for the afterPreflop state.');
    }

    let generalHand = this.states[playerStateIndex].generalHands.find(generalHandString);

    generalHand.forEachSpecificHandInRange(function (specificHand) {
      this.manuallySetWeightOfSpecificHand(specificHand.string, newWeight, presetCommunityCards, playerStateIndex, shortDeck);
    }, this);
  };
  manuallySetWeightOfSpecificHand(specificHandString, newWeight, presetCommunityCards, playerStateIndex, shortDeck) {
    let specificHand = this.states[playerStateIndex].generalHands.find(specificHandString);
    let initialWeight = specificHand.weight;

    if (playerStateIndex === 0) {
      throw new Error('Trying to call manuallySetWeightOfSpecificHand for the afterPreflop state.');
    }

    if (newWeight === initialWeight) {
      return;
    }

    this.setWeightOfSpecificHand(specificHandString, newWeight, presetCommunityCards, playerStateIndex, shortDeck);
    specificHand.updateManuallySetValue(initialWeight, newWeight, this.states[playerStateIndex], presetCommunityCards, playerStateIndex, shortDeck);
  };

  setRangesAccordingToFilters(presetCommunityCards, shortDeck) {
    for (let i = 1; i < this.states.length; i++) {
      this.states[i].setRangeAccordingToFilters(this.states[i - 1].generalHands, presetCommunityCards, shortDeck);
    }
  };

  // five card hands
  setFiveCardHandValuesToDefaults(presetCommunityCards, opponentTightness, settings) {
    let fiveCardHandsThatAreCheckedByDefault;

    this.states.forEach(function (playerState) {
      if (playerState.name !== 'afterPreflop') {
        fiveCardHandsThatAreCheckedByDefault = playerState.getFiveCardHandsThatAreCheckedByDefault(presetCommunityCards, opponentTightness, settings);
        playerState.fiveCardHands.setChecksToDefaults(fiveCardHandsThatAreCheckedByDefault, playerState.name);
        playerState.fiveCardHands.setCollapsedValuesToDefaults(settings);
        playerState.fiveCardHands.setIncludedValuesToDefaults(settings);
      }
    });
  };

  // other
  selectAllSpecificHandsThatUseCardIfGeneralHandWasPreviouslyFullyInRange(card, presetCommunityCards, presetHoleCards, shortDeck) {
    let specificHandStringsThatUseCard = utilities.hands.getAllSpecificHandStringsUsingCard(card);
    let specificHandForAfterPreflop;
    let generalHandForAfterPreflop;

    specificHandStringsThatUseCard.forEach(function (specificHandString) {
      specificHandForAfterPreflop = this.states[0].generalHands.find(specificHandString);
      generalHandForAfterPreflop = specificHandForAfterPreflop.generalHand;

      if (
        generalHandForAfterPreflop.inRange === true &&
        !specificHandForAfterPreflop.possible &&
        !specificHandForAfterPreflop._includesPresetCommunityCard(presetCommunityCards) &&
        !specificHandForAfterPreflop._includesPresetHoleCard(presetHoleCards)
      ) {
        specificHandForAfterPreflop.maxPossibleWeight = 100;
        this.states[0].generalHands.numberOfPossibleSpecificHands++;
        this.setWeightOfSpecificHand(specificHandForAfterPreflop.string, generalHandForAfterPreflop.averageWeight, presetCommunityCards, 0, shortDeck);
      }
    }, this);
  };
  setWhetherSpecificHandsThatUseCardArePossibleOnInitialState(card, presetCommunityCards, presetHoleCards, shortDeck) {
    let specificHandStringsThatUseCard = utilities.hands.getAllSpecificHandStringsUsingCard(card);

    if (shortDeck) {
      specificHandStringsThatUseCard = specificHandStringsThatUseCard.filter(function (specificHandString) {
        return !['5', '4', '3', '2'].includes(specificHandString[0]) && !['5', '4', '3', '2'].includes(specificHandString[2])
      });
    }

    specificHandStringsThatUseCard.forEach(function (specificHandString) {
      let specificHandForAfterPreflop = this.states[0].generalHands.find(specificHandString);

      if (
        !specificHandForAfterPreflop.possible &&
        !specificHandForAfterPreflop._includesPresetCommunityCard(presetCommunityCards) &&
        !specificHandForAfterPreflop._includesPresetHoleCard(presetHoleCards)
      ) {
        specificHandForAfterPreflop.maxPossibleWeight = 100;
        this.states[0].generalHands.numberOfPossibleSpecificHands++;
      }
    }, this);
  };
  hasEmptyRange(street) {
    if (street === 'preflop') {
      return this._hasEmptyRange(0);
    } else if (street === 'flop') {
      return this._hasEmptyRange(1);
    } else if (street === 'turn') {
      return this._hasEmptyRange(2);
    } else if (street === 'river') {
      return this._hasEmptyRange(3);
    }
  };
  _hasEmptyRange(playerStateIndex) {
    return this.states[playerStateIndex].generalHands.numberOfSpecificHandsInRange < 0.05;
  };

  // simulation
  setLastSimulationInfo(presetCommunityCards, presetHoleCards, playerStateIndex) {
    if (playerStateIndex === 0) {
      throw new Error('Trying to call setLastSimulationInfo on the preflop state.');
    }

    let playerState = this.states[playerStateIndex];
    let lastSimulation = playerState.lastSimulation;

    lastSimulation.presetCommunityCards = _.cloneDeep(presetCommunityCards);
    lastSimulation.presetHoleCards = _.cloneDeep(presetHoleCards);
    lastSimulation.nameOfStateUsedForSimulation = utilities.hands.getNameOfStateToUseForSimulation(this, presetCommunityCards, playerState.simulateTo);
    lastSimulation.generalHands = _.cloneDeep(utilities.hands.getGeneralHandsToUseForSimulation(this, presetCommunityCards, playerState.simulateTo));
    lastSimulation.exhaustive = this._isExhaustive(playerState.simulateTo, presetCommunityCards);
  };
  _isExhaustive(simulateTo, presetCommunityCards) {
    if (simulateTo === 'flop') {
      return presetCommunityCards.flopSet;
    }

    else if (simulateTo === 'turn') {
      return presetCommunityCards.turnSet;
    }

    else if (simulateTo === 'river') {
      return presetCommunityCards.riverSet;
    }

    else {
      throw new Error('Invalid value for simulateTo.');
    }
  };
  getGeneralHandsToUseForSimulation(presetCommunityCards, simulateTo) {
    return utilities.hands.getGeneralHandsToUseForSimulation(this, presetCommunityCards, simulateTo);
  };
  getWeightedRangeToUseForSimulation(presetCommunityCards, simulateTo) {
    return this.getGeneralHandsToUseForSimulation(presetCommunityCards, simulateTo).weightedRange;
  };

  // equity calculator
  narrowRange(checkedHands, presetCommunityCards, shortDeck) {
    if (!presetCommunityCards.flopSet) {
      throw new Error('Trying to narrow range without the flop being set.');
    }

    this.states[0].generalHands.forEachSpecificHandInRange(function (specificHand) {
      if (!specificHand.passesThroughFiveCardHandFilter(checkedHands, presetCommunityCards.array, shortDeck)) {
        this.deselectSpecificHand(specificHand.string, presetCommunityCards, 0, shortDeck);
      }
    }, this);
  };

  // handle event
  handleSelectGeneralHandEvent(storeModule, store, generalHandString, triggeredByMouseover, playerIndex, playerStateIndex) {
    let numSpecificHandsToSelect = this._getNumSpecificHandsToSelect(generalHandString, playerStateIndex);

    if (numSpecificHandsToSelect === 0 && !triggeredByMouseover) {
      alert('It isn\'t possible to select this hand because of what you have checked off in the select specific suits menu.');
      setTimeout(function () {
        $('body').trigger('mouseup'); // so that clickAndDragContext in GridSelector component gets set to null
      }, 0);
      return;
    }

    if (playerStateIndex === 0) {
      store.commit(storeModule + '/selectGeneralHand', {
        generalHandString: generalHandString,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    else {
      store.commit(storeModule + '/manuallySelectGeneralHand', {
        generalHandString: generalHandString,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  _getNumSpecificHandsToSelect(generalHandString, playerStateIndex) {
    let generalHand = this.states[playerStateIndex].generalHands.find(generalHandString);

    return generalHand.getSpecificHandStringsToSelect(this.combosToAdd).length;
  };
  handleDeselectGeneralHandEvent(storeModule, store, generalHandString, playerIndex, playerStateIndex) {
    if (playerStateIndex === 0) {
      store.commit(storeModule + '/deselectGeneralHand', {
        generalHandString: generalHandString,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    else {
      store.commit(storeModule + '/manuallyDeselectGeneralHand', {
        generalHandString: generalHandString,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleSelectGeneralHandsEvent(storeModule, store, generalHandStrings, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/selectGeneralHands', {
      generalHandStrings: generalHandStrings,
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
  };
  handleDeselectGeneralHandsEvent(storeModule, store, generalHandStrings, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/deselectGeneralHands', {
      generalHandStrings: generalHandStrings,
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
  };
  handleSelectSpecificHandEvent(storeModule, store, specificHandString, playerIndex, playerStateIndex) {
    if (playerStateIndex === 0) {
      store.commit(storeModule + '/selectSpecificHand', {
        specificHandString: specificHandString,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    else {
      store.commit(storeModule + '/manuallySelectSpecificHand', {
        specificHandString: specificHandString,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleDeselectSpecificHandEvent(storeModule, store, specificHandString, playerIndex, playerStateIndex) {
    if (playerStateIndex === 0) {
      store.commit(storeModule + '/deselectSpecificHand', {
        specificHandString: specificHandString,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    else {
      store.commit(storeModule + '/manuallyDeselectSpecificHand', {
        specificHandString: specificHandString,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleSetRangeToMatchGeneralHandsEvent(storeModule, store, generalHandStrings, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/deselectAllGeneralHands', {
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
    store.commit(storeModule + '/selectGeneralHands', {
      generalHandStrings: generalHandStrings,
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
  };
  handleSetRangeToMatchDoubleSliderEvent(storeModule, store, percentilesObj, playerIndex, playerStateIndex, handRankings) {
    let generalHandStringsToAddFromDoubleSlider = utilities.hands.getGeneralHandStringsToAddFromDoubleSlider(
      percentilesObj.topPercentile,
      percentilesObj.bottomPercentile,
      this.states[playerStateIndex].generalHands,
      handRankings
    );

    this.handleSetRangeToMatchGeneralHandsEvent(storeModule, store, generalHandStringsToAddFromDoubleSlider, playerIndex, playerStateIndex);
  };
  handleSetRangeToMatchSpecificHandStringsEvent(storeModule, store, specificHandStrings, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/deselectAllGeneralHands', {
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
    store.commit(storeModule + '/selectSpecificHands', {
      specificHandStrings: specificHandStrings,
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
  };
  handleSetSpecificHandAsManuallySetEvent(storeModule, store, specificHandString, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/setSpecificHandAsManuallySet', {
      specificHandString: specificHandString,
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
    store.commit(storeModule + '/setRangesAccordingToFilters', playerIndex);
  };
  handleSetSpecificHandAsNotManuallySetEvent(storeModule, store, specificHandString, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/setSpecificHandAsNotManuallySet', {
      specificHandString: specificHandString,
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
    store.commit(storeModule + '/setRangesAccordingToFilters', playerIndex);
  };
  handleApplyExactWeightToGeneralHandEvent(storeModule, store, payload, playerIndex, playerStateIndex) {
    if (playerStateIndex === 0) {
      store.commit(storeModule + '/applyExactWeightToGeneralHand', {
        generalHandString: payload.generalHandString,
        newWeight: payload.newWeight,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    else {
      store.commit(storeModule + '/manuallyApplyExactWeightToGeneralHand', {
        generalHandString: payload.generalHandString,
        newWeight: payload.newWeight,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleSetWeightOfSpecificHandEvent(storeModule, store, payload, playerIndex, playerStateIndex) {
    if (playerStateIndex === 0) {
      store.commit(storeModule + '/setWeightOfSpecificHand', {
        specificHandString: payload.specificHandString,
        newWeight: payload.newWeight,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    else {
      store.commit(storeModule + '/manuallySetWeightOfSpecificHand', {
        specificHandString: payload.specificHandString,
        newWeight: payload.newWeight,
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleSetWeightToApplyToAddedHandsEvent(storeModule, store, weightToApplyToAddedHands, playerIndex) {
    store.commit(storeModule + '/setWeightToApplyToAddedHands', {
      weightToApplyToAddedHands: weightToApplyToAddedHands,
      playerIndex: playerIndex,
    });
  };
  handleFinishedClickAndDrag(storeModule, store, playerIndex, playerStateIndex) {
    if (playerStateIndex > 0) {
      store.commit(storeModule + '/setPercentagesOfSpecificHandsThatAreSelected', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleRemovePlayerEvent(storeModule, store, playerIndex) {
    store.commit(storeModule + '/removePlayer', playerIndex);
  };
  handleClearPlayerEvent(storeModule, store, playerIndex, playerStateIndex) {
    if (['hitCalculator', 'shortDeckHitCalculator', 'guidedAnalysesBluffing', 'guidedAnalysesCalling'].includes(storeModule) && playerStateIndex > 0) {
      // simulate clear all checks click
      store.commit(storeModule + '/uncheckAllFiveCardHandsForGivenState', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
      store.commit(storeModule + '/setRangesAccordingToFilters', playerIndex);

      if (store.state[storeModule].presetCommunityCards.areAllCardsSet(playerStateIndex)) {
        store.commit(storeModule + '/setPercentagesOfSpecificHandsThatAreSelected', {
          playerIndex: playerIndex,
          playerStateIndex: playerStateIndex,
        });
      }

      return;
    }

    if (playerStateIndex === 0) {
      store.commit(storeModule + '/deselectAllGeneralHands', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    } else {
      store.commit(storeModule + '/manuallyDeselectAllGeneralHands', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleAddComboToCombosToAddEvent(storeModule, store, combo, playerIndex) {
    store.commit(storeModule + '/addComboToCombosToAdd', {
      combo: combo,
      playerIndex: playerIndex,
    });
  };
  handleRemoveComboFromCombosToAddEvent(storeModule, store, combo, playerIndex) {
    store.commit(storeModule + '/removeComboFromCombosToAdd', {
      combo: combo,
      playerIndex: playerIndex,
    });
  };
  handleApplyExactWeightToGeneralHandsEvent(storeModule, store, payload, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/applyExactWeightToGeneralHands', {
      generalHandsFromModal: payload.generalHandsFromModal,
      newWeight: payload.newWeight,
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
  };
  handleSetCheckedValueOfFiveCardHandEvent(storeModule, store, payload, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/setCheckedValueOfFiveCardHand', {
      fiveCardHandId: payload.fiveCardHandId,
      newCheckedValue: payload.newCheckedValue,
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
    store.commit(storeModule + '/setRangesAccordingToFilters', playerIndex);

    if (
      (storeModule === 'hitCalculator' && !store.state.hitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex)) ||
      (storeModule === 'shortDeckHitCalculator' && !store.state.shortDeckHitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex))
    ) {
      store.dispatch(storeModule + '/setHitPercentage', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    if (store.state[storeModule].presetCommunityCards.areAllCardsSet(playerStateIndex)) {
      store.commit(storeModule + '/setPercentagesOfSpecificHandsThatAreSelected', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleApplyExactWeightToFiveCardHandEvent(storeModule, store, payload, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/applyExactWeightToFiveCardHand', payload);
    store.commit(storeModule + '/setRangesAccordingToFilters', playerIndex);

    if (
      (storeModule === 'hitCalculator' && !store.state.hitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex)) ||
      (storeModule === 'shortDeckHitCalculator' && !store.state.shortDeckHitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex))
    ) {
      store.dispatch(storeModule + '/setHitPercentage', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    if (store.state[storeModule].presetCommunityCards.areAllCardsSet(playerStateIndex)) {
      store.commit(storeModule + '/setPercentagesOfSpecificHandsThatAreSelected', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleToggleCollapsedValueOfFiveCardHandParentEvent(storeModule, store, fiveCardHandParent) {
    store.commit(storeModule + '/toggleCollapsedValueOfFiveCardHandParent', fiveCardHandParent);
  };
  handleUpdateWeightOfFiveCardHandEvent(storeModule, store, payload, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/updateWeightOfFiveCardHand', {
      fiveCardHandId: payload.fiveCardHandId,
      newWeight: payload.newWeight,
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
    store.commit(storeModule + '/setRangesAccordingToFilters', playerIndex);

    if (
      (storeModule === 'hitCalculator' && !store.state.hitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex)) ||
      (storeModule === 'shortDeckHitCalculator' && !store.state.shortDeckHitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex))
    ) {
      store.dispatch(storeModule + '/setHitPercentage', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    if (store.state[storeModule].presetCommunityCards.areAllCardsSet(playerStateIndex)) {
      store.commit(storeModule + '/setPercentagesOfSpecificHandsThatAreSelected', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleClearAllChecksEvent(storeModule, store, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/uncheckAllFiveCardHandsForGivenState', {
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
    store.commit(storeModule + '/setRangesAccordingToFilters', playerIndex);

    if (
      (storeModule === 'hitCalculator' && !store.state.hitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex)) ||
      (storeModule === 'shortDeckHitCalculator' && !store.state.shortDeckHitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex))
    ) {
      store.dispatch(storeModule + '/setHitPercentage', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    if (store.state[storeModule].presetCommunityCards.areAllCardsSet(playerStateIndex)) {
      store.commit(storeModule + '/setPercentagesOfSpecificHandsThatAreSelected', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleSelectAllChecksEvent(storeModule, store, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/checkAllFiveCardHandsForGivenState', {
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
    store.commit(storeModule + '/setRangesAccordingToFilters', playerIndex);

    if (
      (storeModule === 'hitCalculator' && !store.state.hitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex)) ||
      (storeModule === 'shortDeckHitCalculator' && !store.state.shortDeckHitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex))
    ) {
      store.dispatch(storeModule + '/setHitPercentage', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    if (store.state[storeModule].presetCommunityCards.areAllCardsSet(playerStateIndex)) {
      store.commit(storeModule + '/setPercentagesOfSpecificHandsThatAreSelected', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleSetChecksToDefaultEvent(storeModule, store, opponentTightness, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/setChecksToDefaultsForGivenState', {
      opponentTightness: opponentTightness,
      playerIndex: playerIndex,
      settings: store.state.account.user.settings,
      playerStateIndex: playerStateIndex,
    });
    store.commit(storeModule + '/setRangesAccordingToFilters', playerIndex);

    if (
      (storeModule === 'hitCalculator' && !store.state.hitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex)) ||
      (storeModule === 'shortDeckHitCalculator' && !store.state.shortDeckHitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex))
    ) {
      store.dispatch(storeModule + '/setHitPercentage', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    if (store.state[storeModule].presetCommunityCards.areAllCardsSet(playerStateIndex)) {
      store.commit(storeModule + '/setPercentagesOfSpecificHandsThatAreSelected', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleSetChecksBasedOnUserDefinedFilterEvent(storeModule, store, filterName, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/setChecksAccordingToUserDefinedFilterForGivenState', {
      filterName: filterName,
      userDefinedFilters: store.state.account.user.settings.hitCalculator.userDefinedFilters,
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
    store.commit(storeModule + '/setRangesAccordingToFilters', playerIndex);

    if (
      (storeModule === 'hitCalculator' && !store.state.hitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex)) ||
      (storeModule === 'shortDeckHitCalculator' && !store.state.shortDeckHitCalculator.presetCommunityCards.areAllCardsSet(playerStateIndex))
    ) {
      store.dispatch(storeModule + '/setHitPercentage', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }

    if (store.state[storeModule].presetCommunityCards.areAllCardsSet(playerStateIndex)) {
      store.commit(storeModule + '/setPercentagesOfSpecificHandsThatAreSelected', {
        playerIndex: playerIndex,
        playerStateIndex: playerStateIndex,
      });
    }
  };
  handleFiveCardHandMouseover(storeModule, store, playerIndex, playerStateIndex, matchingSpecificHands) {
    store.commit(storeModule + '/highlightGeneralHandsThatMatchFiveCardHand', {
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
      matchingSpecificHands: matchingSpecificHands,
    });
  };
  handleFiveCardHandMouseleave(storeModule, store, playerIndex, playerStateIndex) {
    store.commit(storeModule + '/removeFiveCardHandHighlights', {
      playerIndex: playerIndex,
      playerStateIndex: playerStateIndex,
    });
  };

  // short deck
  adjustForShortDeck() {
    this.states.forEach(function (playerState) {
      playerState.generalHands.adjustForShortDeck();
    });
  };
};



// WEBPACK FOOTER //
// ./src/services/player/Player.js